import {useCallback, useEffect, useRef, useState} from "react";
import type {SessionContext} from "./SessionContext";
import {SSO_ACCESS_TOKEN_PARAM, SSO_VISIBILITY_PARAM, personaOptions,
        ssoAuthenticate} from "./ssoAuthenticate";
import {postLoginRedirect} from "./postLoginRedirect";

export type SsoLoginState = {
    // True while the foreign token is being exchanged for a native session.
    processing: boolean;
    // True once the session is established but the user must still choose a persona (they have >1).
    // The app should render the persona prompt instead of the main UI while this is true.
    selectingPersona: boolean;
    // Called by the persona prompt after it has set the active visibility, to release the app.
    onPersonaSelected: () => void;
    // True when the hand-off could not be exchanged for a session -- almost always a source tab whose
    // token had already expired. The app must render an explanation; falling through to the normal
    // logged-out landing shows the user a blank screen after they clicked a link.
    failed: boolean;
    // On a failure, whether it was ours (a 5xx or no response) rather than a dead link.
    // The explanation screen says a different, true thing in each case.
    fault: boolean;
    // The platform support code off the failed response, when it carried one.
    faultCode: string | null;
    // The account the source app proposed, so the chooser can default to it when the hint
    // was not enough on its own. Null when none was offered or it named no account here.
    preferredAccountUsid: string | null;
    // The accounts to choose between when the email belongs to more than one, or the one
    // account whose personas must be chosen between. Null when there is nothing to pick.
    chooserAccounts: Record<string, any> | null;
    // True while a chosen account/persona is being re-submitted.
    chooserSubmitting: boolean;
    // Called by the chooser with `<accountUSID>:<Mode>:<USID>`.
    onAccountChosen: (visibility: string) => void;
    // Called by that explanation to open this app's own login and release the failure screen.
    onSignIn: () => void;
};

// On initial load, if a source application handed off a foreign access token in the URL, exchange it
// for a native session before the app renders, then ALWAYS hold in a persona-selection phase and let
// the user choose.
//
// Always, not "when there is more than one". A persona belongs to this app's account structure, and
// the structures do not line up between apps -- so arriving from another one tells us nothing about
// who the user is here, including when there happens to be a single candidate. The only case that
// does not prompt is having no personas at all, where there is nothing to show and nothing to pick.
export function useSsoLogin(sessionContext: SessionContext): SsoLoginState {
    const [processing, setProcessing] = useState<boolean>(
        () => new URLSearchParams(window.location.search).has(SSO_ACCESS_TOKEN_PARAM),
    );
    const [selectingPersona, setSelectingPersona] = useState<boolean>(false);
    const [failed, setFailed] = useState<boolean>(false);
    const [fault, setFault] = useState<boolean>(false);
    const [faultCode, setFaultCode] = useState<string | null>(null);
    const [chooserAccounts, setChooserAccounts] = useState<Record<string, any> | null>(null);
    const [chooserSubmitting, setChooserSubmitting] = useState<boolean>(false);
    const chooserShownRef = useRef(false);
    const [preferredAccountUsid, setPreferredAccountUsid] = useState<string | null>(null);

    // THE TOKEN OUTLIVES THE URL. It is stripped from the address bar below so it is not
    // reprocessed or leaked, but the chooser needs it again for the re-submit.
    const tokenRef = useRef<string | null>(null);

    // Guards against React StrictMode's double effect invocation in development
    // so the token is exchanged exactly once.
    const startedRef = useRef(false);

    useEffect(() => {
        if (startedRef.current) return;
        startedRef.current = true;

        const params = new URLSearchParams(window.location.search);
        const token = params.get(SSO_ACCESS_TOKEN_PARAM);
        if (!token) {
            setProcessing(false);
            return;
        }

        (async () => {
            let established = false;
            let settledByHint = false;
            try {
                tokenRef.current = token;

                // THE HINT IS TRIED FIRST AND NEVER INSISTED ON. The source app proposes the
                // account and persona it was being used under; the exchange verifies the persona
                // against the list it would itself have offered and refuses with CC-5OUE-S3RG
                // when it does not belong here -- a Position carries a FOCUS_AREA and QMS offers
                // only its own, so a persona that is fine in SBOM may name nothing in QMS.
                //
                // ANY refusal falls back to the plain exchange rather than being shown to the
                // user, because a rejected hint is not a failed sign-in: it is a question that
                // still has to be asked. The fallback is what the app did before the hint
                // existed, so the worst case is exactly the old behaviour.
                const hint = params.get(SSO_VISIBILITY_PARAM);
                if (hint) setPreferredAccountUsid(hint.split(":")[0] || null);

                let result = hint ? await ssoAuthenticate(token, sessionContext, hint) : null;
                // ACCEPTED MEANS SETTLED. The exchange checked the persona against the list it
                // would itself have offered, and ssoAuthenticate set it. Prompting now would ask
                // the user to re-enter the answer they arrived holding, which is the entire thing
                // carrying it was meant to avoid.
                settledByHint = !!result?.established;
                if (!result || !result.established) {
                    if (result) {
                        // eslint-disable-next-line no-console
                        console.info("[SSO] the proposed account/persona was not accepted here; asking");
                    }
                    result = await ssoAuthenticate(token, sessionContext);
                }
                established = result.established;
                if (!result.established && "chooser" in result) {
                    // Not a failure: the exchange needs to be told which account.
                    chooserShownRef.current = true;
                    setChooserAccounts(result.chooser);
                } else if (!result.established) {
                    setFault(result.fault);
                    setFaultCode(result.code ?? null);
                }
            } catch (e) {
                // A throw here never reached the server, or never got an answer back.
                setFault(true);
                // Fall through to the normal (logged-out) landing on any failure.
                // eslint-disable-next-line no-console
                console.warn("[SSO] sign-in hand-off failed", e);
            } finally {
                // Strip the hand-off param from the URL so it isn't reprocessed or leaked.
                params.delete(SSO_ACCESS_TOKEN_PARAM);
                params.delete(SSO_VISIBILITY_PARAM);
                const search = params.toString();
                const newUrl = window.location.pathname + (search ? `?${search}` : "") + window.location.hash;
                window.history.replaceState(null, "", newUrl);
                // Prompt whenever there is anything to prompt with. ssoAuthenticate no longer sets
                // a visibility at all, so the only question left is whether this user has any
                // personas here -- with none there is nothing to choose and the app simply lands.
                if (!established && chooserShownRef.current) {
                    // The chooser is up; it owns what happens next.
                } else if (established && settledByHint) {
                    // Nothing left to ask: honour a stashed destination and let the app render.
                    const destination = postLoginRedirect("/");
                    if (destination) window.location.href = destination;
                } else if (established && personaOptions(sessionContext.getAccount()).length > 0) {
                    setSelectingPersona(true);
                } else if (!established) {
                    // A token was handed over and did not work. Say so -- see SsoHandoffFailed.
                    setFailed(true);
                } else if (established) {
                    // No persona prompt needed → honor a stashed post-login destination (future-proofing).
                    const destination = postLoginRedirect("/");
                    if (destination) window.location.href = destination;
                }
                setProcessing(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Opens this app's own login and clears the failure screen. NavBar reads ShowLoginDialog off the
    // session on its update event, so setting it here is what actually raises the dialog.
    const onSignIn = useCallback(() => {
        sessionContext.setShowLoginDialog(true);
        setFailed(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Re-submits the exchange naming the account and persona the user picked. The token was
    // kept in a ref because the URL it arrived in was cleaned up before this point.
    const onAccountChosen = useCallback(async (visibility: string) => {
        const token = tokenRef.current;
        if (!token || chooserSubmitting) return;
        setChooserSubmitting(true);
        try {
            const result = await ssoAuthenticate(token, sessionContext, visibility);
            if (result.established) {
                // ssoAuthenticate set the visibility from the choice, so there is nothing left to
                // prompt for -- release the app.
                setChooserAccounts(null);
                chooserShownRef.current = false;
                const destination = postLoginRedirect("/");
                if (destination) window.location.href = destination;
                return;
            }
            // A second refusal is a real failure; there is nothing further to pick.
            setChooserAccounts(null);
            chooserShownRef.current = false;
            setFault("fault" in result ? result.fault : true);
            setFaultCode("code" in result ? result.code ?? null : null);
            setFailed(true);
        } finally {
            setChooserSubmitting(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chooserSubmitting]);

    const onPersonaSelected = useCallback(() => {
        setSelectingPersona(false);
        // After the user picks a persona, honor a stashed post-login destination (future-proofing).
        const destination = postLoginRedirect("/");
        if (destination) window.location.href = destination;
    }, []);

    return {processing, selectingPersona, onPersonaSelected, failed, fault, faultCode,
            chooserAccounts, chooserSubmitting, onAccountChosen,
            preferredAccountUsid, onSignIn};
}
